/**
=========================================================
* Comparedly - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Comparedly components
import MDButton from "components/MDButton";
import FeatureUpgrade from "layouts/components/Upgrade/feature"; // ✅ Import FeatureUpgrade

// Hooks
import { useAiUsage } from "hooks/useAiUsage";
import { useChartAnalysis } from "hooks/useChartAnalysis";

// Sub-components
import AssistantMenu from "./AssistantMenu";

function ChartAssistant({ chartData, title, context, rawData, sx = {}, entitlements }) {
    const [menu, setMenu] = useState(null);
    const [query, setQuery] = useState("");

    // Custom Hooks
    const { usage, updateUsage } = useAiUsage();
    const {
        analyze,
        loading,
        error,
        response,
        resetState,
        showUpgrade,
        setShowUpgrade
    } = useChartAnalysis({
        chartData,
        title,
        context,
        rawData,
        entitlements,
        updateUsage
    });

    const openMenu = (event) => setMenu(event.currentTarget);
    const closeMenu = () => {
        setMenu(null);
        resetState();
        setQuery("");
    };

    const handleAnalyze = () => {
        analyze(query);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAnalyze();
        }
    };

    return (
        <>
            {/* Floating Trigger Button inside the chart container */}
            <MDButton
                variant="contained"
                color="accent"
                size="small"
                onClick={openMenu}
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 15,
                    zIndex: 99,
                    px: 1,
                    textTransform: "none",
                    overflow: "hidden", // Required for shimmer

                    // Button Wiggle
                    animation: Boolean(menu) ? "none" : "button-wiggle 3s infinite linear",
                    "@keyframes button-wiggle": {
                        "0%, 80%": { transform: "rotate(0deg)" },
                        "85%": { transform: "rotate(-3deg)" },
                        "90%": { transform: "rotate(3deg)" },
                        "95%": { transform: "rotate(-3deg)" },
                        "100%": { transform: "rotate(0deg)" },
                    },

                    // Premium Shimmer Effect
                    "&::after": {
                        content: Boolean(menu) ? "none" : '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "200%",
                        height: "100%",
                        background: "linear-gradient(120deg, transparent, rgba(255,255,255, 0.6), transparent)",
                        animation: Boolean(menu) ? "none" : "shimmer 3s infinite linear",
                    },
                    "@keyframes shimmer": {
                        "0%": { left: "-100%" },
                        "20%": { left: "100%" }, // Fast pass
                        "100%": { left: "100%" }, // Wait
                    },
                    ...sx, // ✅ OVERRIDE with passed styles
                }}
            >
                <Icon
                    sx={{
                        mr: 0.5,
                        fontSize: "1rem !important",
                        animation: Boolean(menu) ? "none" : "wiggle 3s infinite linear",
                        "@keyframes wiggle": {
                            "0%, 80%": { transform: "rotate(0deg)" },
                            "85%": { transform: "rotate(-15deg)" },
                            "90%": { transform: "rotate(15deg)" },
                            "95%": { transform: "rotate(-15deg)" },
                            "100%": { transform: "rotate(0deg)" },
                        }
                    }}
                >
                    auto_awesome
                </Icon>
                Ask AI
            </MDButton>

            <AssistantMenu
                menu={menu}
                closeMenu={closeMenu}
                usage={usage}
                entitlements={entitlements}
                response={response}
                loading={loading}
                error={error}
                query={query}
                setQuery={setQuery}
                handleKeyDown={handleKeyDown}
                handleAnalyze={handleAnalyze}
            />

            {/* 🔒 Feature Upgrade Popup */}
            <FeatureUpgrade
                open={showUpgrade}
                onClose={() => setShowUpgrade(false)}
                featureName="AI Powered Insight"
                planRequired={entitlements?.requiredPlans?.["AI Powered Insight"] || "Higher Plan"}
            />
        </>
    );
}

ChartAssistant.propTypes = {
    chartData: PropTypes.object,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    context: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    rawData: PropTypes.any, // ✅ New prop type
    sx: PropTypes.object, // ✅ New prop type
    entitlements: PropTypes.object, // ✅ Pass entitlements
};

export default ChartAssistant;
